import React from "react";
import {
  FaChartLine,
  FaMousePointer,
  FaSearch,
  FaUsers,
  FaCogs,
  FaLightbulb,
  FaCheck,
  FaArrowRight,
  FaMobileAlt,
  FaClipboardCheck,
  FaSync,
  FaMagic,
  FaPalette,
  FaFilter
} from "react-icons/fa";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import conversionBg from "../../assets/ConversionRate.jpg";

const ConversionOptimization = () => {
  const services = [
    {
      icon: FaSearch,
      title: "CRO Audit & Analysis",
      description: "Comprehensive analysis of your conversion funnel and user journey to identify optimization opportunities",
      features: ["Funnel Analysis", "Heatmap Tracking", "Session Recordings", "User Surveys", "Competitor Analysis", "Technical Audit"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaMousePointer,
      title: "A/B & Multivariate Testing",
      description: "Data-driven testing of design, copy, and layout variations to maximize conversions",
      features: ["Hypothesis Creation", "Test Implementation", "Statistical Analysis", "Results Interpretation", "Iterative Testing", "Personalization"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaUsers,
      title: "User Experience Optimization",
      description: "Enhance user experience to reduce friction and improve conversion rates",
      features: ["UX Analysis", "Usability Testing", "User Flow Mapping", "Interface Design", "Mobile Optimization", "Accessibility Review"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaClipboardCheck,
      title: "Landing Page Optimization",
      description: "Design and optimize high-converting landing pages that drive specific actions",
      features: ["Copy Optimization", "Visual Hierarchy", "Form Design", "Trust Elements", "Call-to-Action Testing", "Mobile Responsiveness"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaCogs,
      title: "Technical Optimization",
      description: "Improve technical performance to eliminate barriers to conversion",
      features: ["Page Speed Optimization", "Mobile Responsiveness", "Cross-Browser Testing", "Security Enhancements", "Schema Markup", "Core Web Vitals"],
      color: "from-orange-500 to-amber-500"
    },
    {
      icon: FaChartLine,
      title: "Analytics & Reporting",
      description: "Comprehensive tracking and data-driven insights for continuous improvement",
      features: ["Conversion Tracking", "Funnel Visualization", "KPI Monitoring", "ROI Analysis", "Reporting Dashboards", "Predictive Analytics"],
      color: "from-orange-500 to-amber-500"
    }
  ];

  const process = [
    {
      step: "01",
      title: "Discovery & Analysis",
      description: "Comprehensive review of current performance and identification of optimization opportunities",
      icon: FaSearch
    },
    {
      step: "02",
      title: "Hypothesis Creation",
      description: "Develop data-backed hypotheses for testing based on user behavior insights",
      icon: FaLightbulb
    },
    {
      step: "03",
      title: "Test Design & Setup",
      description: "Design and implement A/B or multivariate tests with proper tracking",
      icon: FaCogs
    },
    {
      step: "04",
      title: "Implementation",
      description: "Roll out changes based on winning test variations and insights",
      icon: FaMagic
    },
    {
      step: "05",
      title: "Monitoring & Analysis",
      description: "Continuously monitor performance and analyze results for further optimization",
      icon: FaChartLine
    },
    {
      step: "06",
      title: "Iterative Optimization",
      description: "Apply learnings to develop new hypotheses and continue optimization cycles",
      icon: FaSync
    }
  ];

  const optimizationAreas = [
    {
      category: "Page Elements",
      items: ["Headlines & Copy", "Call-to-Actions", "Forms & Fields", "Images & Videos", "Trust Indicators", "Social Proof"],
      icon: FaPalette
    },
    {
      category: "User Experience",
      items: ["Navigation & Flow", "Mobile Experience", "Page Speed", "Accessibility", "Error Handling", "Personalization"],
      icon: FaUsers
    },
    {
      category: "Testing Methods",
      items: ["A/B Testing", "Multivariate Testing", "Split URL Testing", "Personalization Tests", "Seasonal Testing", "Personalized Experiences"],
      icon: FaMousePointer
    }
  ];

  const features = [
    {
      title: "Advanced Analytics",
      description: "In-depth tracking of user behavior, conversion paths, and micro-conversions",
      icon: FaChartLine
    },
    {
      title: "Heatmap Analysis",
      description: "Visual representation of user clicks, scrolls, and attention patterns",
      icon: FaSearch
    },
    {
      title: "Mobile Optimization",
      description: "Ensure perfect performance and conversions across all mobile devices",
      icon: FaMobileAlt
    },
    {
      title: "Segmentation & Targeting",
      description: "Deliver personalized experiences based on user behavior and demographics",
      icon: FaFilter
    }
  ];

  const platforms = [
    {
      name: "Google Optimize",
      description: "Free A/B testing platform integrated with Google Analytics",
      strength: "Seamless Google Analytics integration"
    },
    {
      name: "Optimizely",
      description: "Enterprise-grade experimentation platform with advanced features",
      strength: "Advanced statistical engine"
    },
    {
      name: "VWO",
      description: "Comprehensive conversion optimization platform with heatmaps",
      strength: "All-in-one testing suite"
    },
    {
      name: "Hotjar",
      description: "Behavior analytics platform with heatmaps and recordings",
      strength: "User behavior insights"
    }
  ];

  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <div className="min-h-screen">
        {/* Hero Section */}
        <div
          className="relative bg-cover bg-center bg-no-repeat text-white py-24 overflow-hidden"
          style={{ backgroundImage: `url(${conversionBg})` }}
        >
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-80 h-80 bg-orange-500 rounded-full filter blur-3xl opacity-20"></div>
            <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-amber-500 rounded-full filter blur-3xl opacity-20"></div>
          </div>

          <div className="container mx-auto px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-6xl font-bold mb-6 bg-gradient-to-r from-white to-white bg-clip-text text-transparent pb-3">
                Conversion Optimization
              </h1>
              <p className="text-xl text-gray-200 mb-10 leading-relaxed max-w-3xl mx-auto">
                Transform visitors into customers. Data-driven optimization strategies to maximize your website's conversion potential and boost revenue.
              </p>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-orange-500/10 to-transparent"></div>
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Conversion Optimization Services
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed">
                Comprehensive CRO solutions to maximize your website's performance and revenue
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-gray-700 hover:border-orange-500/50 hover:transform hover:-translate-y-2"
                >
                  <div className="flex items-center justify-between mb-6">
                    <div className={`bg-gradient-to-r ${service.color} p-4 rounded-2xl text-white text-2xl shadow-lg`}>
                      <service.icon />
                    </div>
                    <div className="text-2xl font-bold text-white">{service.title}</div>
                  </div>
                  <p className="text-gray-300 leading-relaxed text-lg mb-6">
                    {service.description}
                  </p>
                  <div className="space-y-3">
                    {service.features.map((feature, featureIndex) => (
                      <div
                        key={featureIndex}
                        className="flex items-center text-gray-300 group/item hover:text-orange-400 transition-colors duration-200"
                      >
                        <div className="bg-orange-500/20 p-1 rounded-lg mr-3 group-hover/item:bg-orange-500/30 transition-colors duration-200">
                          <FaCheck className="text-orange-400 text-sm" />
                        </div>
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Optimization Areas Section */}
        <div className="pt-10 pb-20 bg-white relative">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-amber-50 opacity-30"></div>
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Optimization Focus Areas
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Targeted strategies to improve specific aspects of your conversion funnel
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {optimizationAreas.map((area, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 group hover:border-orange-200"
                >
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-2xl p-4 rounded-2xl inline-flex mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <area.icon />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">
                    {area.category}
                  </h3>
                  <div className="space-y-3">
                    {area.items.map((item, itemIndex) => (
                      <div
                        key={itemIndex}
                        className="flex items-center text-gray-700 group/item hover:text-orange-600 transition-colors duration-200"
                      >
                        <div className="bg-green-100 p-1 rounded-lg mr-3 group-hover/item:bg-green-200 transition-colors duration-200">
                          <FaCheck className="text-green-600 text-sm" />
                        </div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Key Features Section */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative">
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Advanced CRO Capabilities
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Leverage cutting-edge conversion optimization tools and methodologies
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 hover:shadow-2xl transition-all duration-500 border border-gray-700 hover:border-orange-500/50"
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xl p-3 rounded-xl shadow-lg">
                      <feature.icon />
                    </div>
                    <h3 className="text-2xl font-bold text-white">{feature.title}</h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed text-lg">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Platform Expertise */}
        <div className="pt-10 pb-20 bg-white relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-6">
                Platform Expertise
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                We're experts in all major conversion optimization platforms
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {platforms.map((platform, index) => (
                <div
                  key={index}
                  className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 group hover:border-orange-200 text-center"
                >
                  <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xl p-4 rounded-2xl inline-flex mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <FaChartLine />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{platform.name}</h3>
                  <p className="text-gray-600 text-sm mb-3">{platform.description}</p>
                  <div className="text-orange-600 text-sm font-semibold">
                    {platform.strength}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Process Section */}
        <div className="pt-10 pb-20 bg-gradient-to-br from-gray-900 to-black text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-orange-500/10 to-transparent"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full -translate-y-48 translate-x-48"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/5 rounded-full translate-y-48 -translate-x-48"></div>
          
          <div className="container mx-auto px-6 relative">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-white mb-6">
                Our Conversion Optimization Process
              </h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                A systematic approach to maximizing your website's conversion potential
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {process.map((step, index) => (
                <div key={index} className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                  <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700 text-white rounded-2xl p-8 h-full transform group-hover:scale-105 transition-all duration-500">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="text-3xl font-bold text-orange-400 bg-orange-500/10 px-4 py-2 rounded-xl border border-orange-500/20">
                        {step.step}
                      </div>
                      <div className="text-orange-400">
                        <step.icon className="text-2xl" />
                      </div>
                    </div>
                    <h3 className="text-2xl font-bold mb-4 text-white">{step.title}</h3>
                    <p className="text-gray-300 leading-relaxed text-lg">
                      {step.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="relative pt-10 pb-10 bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1/2 bg-gradient-to-b from-orange-500/10 to-transparent"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-black opacity-40"></div>
          
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute -top-20 -left-20 w-80 h-80 bg-orange-500 rounded-full filter blur-3xl opacity-10"></div>
            <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-amber-500 rounded-full filter blur-3xl opacity-10"></div>
          </div>
          
          <div className="container mx-auto px-6 text-center relative z-10">
            <h2 className="text-5xl font-bold text-white mb-8">
              Ready to Boost Your Conversions?
            </h2>
            <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
              Let us help you transform your website into a high-converting machine. 
              From audit to implementation, we'll optimize every step of your customer journey.
            </p>
            <div className="flex justify-center items-center mt-6">
              <button 
                onClick={() => (window.location.href = "/contact-us")}
                className="group bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-12 py-5 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-2xl hover:shadow-orange-500/25 flex items-center gap-3"
              >
                Start Free CRO Audit
                <FaArrowRight className="group-hover:translate-x-2 transition-transform duration-200" />
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ConversionOptimization;